import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { InvoiceSummary } from "@/pages/PayInvoices/components/InvoiceSummary";
import { InvoiceCard } from "@/pages/PayInvoices/components/InvoiceCard";
import {
  ZuoraPaymentMethodTypes,
  Invoice,
  ZuoraPaymentMethod,
} from "@/api/opb/opbTypes";
import { ACHInfo } from "@/pages/PaymentMethods/components/ACHInfo";
import { CreditCardInfo } from "@/pages/PaymentMethods/components/CreditCardInfo";
import { Button, TextButton } from "@industriousoffice/react-components";
import { getFullPath } from "@/router/utils/getFullPath";
import { PadInfo } from "@/pages/PaymentMethods/components/PadInfo";
import { BacsInfo } from "@/pages/PaymentMethods/components/BacsInfo";
import { SepaInfo } from "@/pages/PaymentMethods/components/SepaInfo";
import { FAQ_LINK } from "@/constants";
import { SplittedPage } from "@/components/SplittedPage/SplittedPage";

interface PaymentConfirmationProps {
  invoices: Invoice[];
  paymentMethod: ZuoraPaymentMethod;
  currencyCode: string;
  locationName: string;
  creditCardFeePaid?: string;
  totalPaymentPaid: string;
  accountId: string;
}

export function PaymentConfirmation({
  invoices,
  paymentMethod,
  currencyCode,
  locationName,
  creditCardFeePaid,
  totalPaymentPaid,
  accountId,
}: PaymentConfirmationProps) {
  const navigate = useNavigate();
  const { t } = useTranslation("payInvoices");

  const onNavigateToDashboard = () => {
    navigate(getFullPath("/"));
  };
  const onOpenFAQ = () => window.open(FAQ_LINK);

  return (
    <SplittedPage>
      <SplittedPage.Left>
        <div
          className="mt-10 text-2xl font-bold sm:text-3xl sm:mb-4"
          data-testid="pay_invoices_confirmation-title"
        >
          {t("Thank you for your payment")}
        </div>
        <div>{t("Your selected invoice(s) have been paid.")}</div>
        <div className="mt-4 sm:rounded-sm sm:border sm:border-grey-60 sm:mt-8">
          {invoices.map((invoice, i) => (
            <div className="mb-4 sm:mb-0" key={invoice.id}>
              <InvoiceCard
                invoice={invoice}
                name={locationName}
                currencyCode={currencyCode}
                isProcessing
                accountId={accountId}
                isLast={i === invoices.length - 1}
              />
            </div>
          ))}
        </div>
        <div className="flex px-4 py-4 mt-6 border rounded-lg cursor-default sm:mt-10 border-grey-60">
          {(() => {
            switch (paymentMethod.type) {
              case ZuoraPaymentMethodTypes.ACH:
                return <ACHInfo paymentMethod={paymentMethod} />;
              case ZuoraPaymentMethodTypes.CreditCard:
                return <CreditCardInfo paymentMethod={paymentMethod} />;
              case ZuoraPaymentMethodTypes.Pad:
                return <PadInfo paymentMethod={paymentMethod} />;
              case ZuoraPaymentMethodTypes.Bacs:
                return <BacsInfo paymentMethod={paymentMethod} />;
              case ZuoraPaymentMethodTypes.Sepa:
                return <SepaInfo paymentMethod={paymentMethod} />;
              default:
                return null;
            }
          })()}
        </div>
        <div className="mt-8 mb-4 text-xl font-bold sm:text-2xl sm:mt-16 sm:mb-0">
          {t("Make another payment")}
        </div>
        <div>{t("Review your current balance and unpaid invoices.")}</div>
        <Button
          onClick={onNavigateToDashboard}
          className="py-5 mt-4 px-10! sm:w-fit w-full"
          data-testid="pay_invoices_confirmation-dashboard-button"
        >
          {t("Go to Billing Dashboard")}
        </Button>
      </SplittedPage.Left>
      <SplittedPage.Right className="h-full">
        <InvoiceSummary
          invoices={invoices}
          creditCardFeeToPay={creditCardFeePaid}
          totalPaymentToPay={totalPaymentPaid}
          currencyCode={currencyCode}
          className="flex flex-col h-full"
          footer={
            <div className="flex flex-col h-full">
              {creditCardFeePaid && (
                <div className="mt-6 font-bold">
                  {t(
                    "You will receive a separate invoice for the card processing fee in your billing portal within a few days."
                  )}
                </div>
              )}
              <div className="pb-24 mt-auto">
                <div className="text-xl font-bold sm:text-2xl">
                  {t("Questions?")}
                </div>
                <div className="mt-3">
                  <TextButton
                    onClick={onOpenFAQ}
                    className="ml-1 border-b border-ocean-60 text-base!"
                    testId="pay_invoices_summary-faq-button"
                  >
                    {t("FAQ page")}
                  </TextButton>
                  <span>{t("Contact us")}</span>
                </div>
              </div>
            </div>
          }
        />
      </SplittedPage.Right>
    </SplittedPage>
  );
}
